import React, { useState, useEffect } from 'react';
import {
  Shield, Star, Flame, Heart, Eye, Rocket, Crown,
  Compass, Puzzle, MapPin, Check
} from 'lucide-react';
import { getScore } from '../services/gamificationCache';

const BADGE_ICONS = {
  'puzzle': Puzzle, 'shield-check': Shield, 'flame': Flame, 'star': Star,
  'eye': Eye, 'heart': Heart, 'rocket': Rocket, 'crown': Crown,
  'compass': Compass, 'map-pin': MapPin, 'image': Eye, 'phone': Check,
};

const LEVEL_COLORS = {
  1: '#9ca3af', 2: '#3b82f6', 3: '#f59e0b', 4: '#7c3aed', 5: '#eab308',
};

/**
 * Compact badge display for business cards.
 * Shows up to `max` badges inline.
 */
export function BusinessBadgesInline({ businessId, max = 3 }) {
  const [badges, setBadges] = useState(null);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    getScore(businessId)
      .then((data) => {
        if (cancelled || !data) return;
        setBadges({
          badges: data.badges_active || [],
          level: data.level || 1,
          score: data.score || 0,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [businessId]);

  if (!badges || badges.badges.length === 0) return null;

  const shown = badges.badges.slice(0, max);
  const remaining = badges.badges.length - max;
  const color = LEVEL_COLORS[badges.level] || '#9ca3af';

  return (
    <div className="flex flex-wrap gap-1" data-testid={`badges-inline-${businessId}`}>
      {shown.map(badgeId => {
        const label = badgeId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return (
          <span
            key={badgeId}
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium border"
            style={{ borderColor: color + '40', backgroundColor: color + '10', color }}
            data-testid={`badge-pill-${badgeId}`}
          >
            <Check className="w-2.5 h-2.5" />
            {label}
          </span>
        );
      })}
      {remaining > 0 && (
        <span className="text-[10px] text-muted-foreground self-center">+{remaining}</span>
      )}
    </div>
  );
}

/**
 * Level indicator for business cards.
 * Shows a small colored indicator with level name.
 */
export function BusinessLevelBadge({ businessId }) {
  const [level, setLevel] = useState(null);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    getScore(businessId)
      .then((data) => {
        if (cancelled || !data) return;
        setLevel({ level: data.level || 1, score: data.score || 0 });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [businessId]);

  if (!level) return null;

  const LEVEL_NAMES = {
    1: 'Novo', 2: 'Em Movimento', 3: 'Estruturado', 4: 'Destaque', 5: 'Referência',
  };

  const LEVEL_ICONS = { 1: Compass, 2: Compass, 3: Star, 4: Rocket, 5: Crown };
  const Icon = LEVEL_ICONS[level.level] || Compass;
  const color = LEVEL_COLORS[level.level] || '#9ca3af';
  const name = LEVEL_NAMES[level.level] || '';

  if (level.level < 3) return null; // Only show for level 3+

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border"
      style={{ borderColor: color + '50', backgroundColor: color + '15', color }}
      data-testid={`level-badge-${businessId}`}
    >
      <Icon className="w-3 h-3" />
      {name}
    </span>
  );
}
